(() => {
  const MAX_FILE_BYTES = 12 * 1024 * 1024;
  const ACCEPTED_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
  ]);

  class DpwPreviewService {
    constructor(root) {
      this.root = root;
      this.formSection = root.querySelector('[data-dps-form-section]');
      this.shell = root.querySelector('[data-dps-photo-form]');
      this.form = this.shell?.querySelector('form') || null;
      this.fileInput = this.form?.querySelector('[data-dps-photo-input]') || null;
      this.photoUrl = this.form?.querySelector('[data-dps-photo-url]') || null;
      this.submitButton = this.form?.querySelector('[data-dps-submit]') || null;
      this.errorBox = this.form?.querySelector('[data-dps-upload-error]') || null;
      this.preview = this.form?.querySelector('[data-dps-photo-preview]') || null;
      this.previewImage = this.form?.querySelector('[data-dps-photo-preview-image]') || null;
      this.previewName = this.form?.querySelector('[data-dps-photo-preview-name]') || null;
      this.endpoint = root.dataset.uploadEndpoint || '';
      this.previewObjectUrl = '';
      this.uploading = false;
    }

    init() {
      this.root.querySelectorAll('[data-dps-scroll-form]').forEach((button) => {
        button.addEventListener('click', () => {
          this.formSection?.scrollIntoView({ behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
          window.setTimeout(() => this.form?.querySelector('input:not([type="hidden"])')?.focus(), 350);
        });
      });

      if (!this.form || !this.fileInput || !this.photoUrl || !this.submitButton || !this.errorBox) return;

      this.submitButton.disabled = false;
      this.fileInput.addEventListener('change', () => this.handleFileChange());
      this.form.addEventListener('invalid', (event) => this.handleInvalid(event), true);
      this.form.addEventListener('input', (event) => this.clearInvalid(event));
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    handleInvalid(event) {
      if (!(event.target instanceof HTMLElement)) return;
      event.target.setAttribute('aria-invalid', 'true');
    }

    clearInvalid(event) {
      if (!(event.target instanceof HTMLElement)) return;
      if ('validity' in event.target && event.target.validity?.valid) {
        event.target.removeAttribute('aria-invalid');
      }
    }

    handleFileChange() {
      this.clearError();
      this.photoUrl.value = '';
      const file = this.fileInput.files?.[0];

      if (this.previewObjectUrl) {
        URL.revokeObjectURL(this.previewObjectUrl);
        this.previewObjectUrl = '';
      }

      if (!file) {
        this.hidePreview();
        return;
      }

      const error = this.validateFile(file);
      if (error) {
        this.fileInput.value = '';
        this.fileInput.setAttribute('aria-invalid', 'true');
        this.showError(error, this.fileInput);
        this.hidePreview();
        return;
      }

      this.fileInput.removeAttribute('aria-invalid');
      this.previewObjectUrl = URL.createObjectURL(file);
      if (this.previewImage) this.previewImage.src = this.previewObjectUrl;
      if (this.previewName) this.previewName.textContent = file.name;
      if (this.preview) this.preview.hidden = false;
    }

    validateFile(file) {
      if (!ACCEPTED_TYPES.has(file.type)) {
        return 'Gebruik een JPG-, PNG-, WebP-, HEIC- of HEIF-bestand.';
      }
      if (file.size > MAX_FILE_BYTES) {
        return 'De foto is groter dan 12 MB. Kies een kleiner bestand.';
      }
      return '';
    }

    hidePreview() {
      if (this.preview) this.preview.hidden = true;
      if (this.previewImage) this.previewImage.removeAttribute('src');
      if (this.previewName) this.previewName.textContent = '';
    }

    async handleSubmit(event) {
      if (this.uploading || this.photoUrl.value) return;
      event.preventDefault();

      if (!this.form.reportValidity()) return;
      const file = this.fileInput.files?.[0];
      if (!file) {
        this.showError('Kies eerst een foto.', this.fileInput);
        return;
      }

      const fileError = this.validateFile(file);
      if (fileError) {
        this.showError(fileError, this.fileInput);
        return;
      }
      if (!this.endpoint) {
        this.showError('De foto-upload is tijdelijk niet beschikbaar. Gebruik het e-mailadres onder het formulier.');
        return;
      }

      this.setBusy(true);
      this.clearError();
      try {
        const upload = new FormData();
        upload.set('photo', file, file.name);
        const response = await fetch(this.endpoint, {
          method: 'POST',
          body: upload,
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || typeof payload.photoUrl !== 'string') {
          throw new Error(payload.error || 'De foto kon niet worden geüpload. Probeer het opnieuw.');
        }

        this.photoUrl.value = payload.photoUrl;
        this.fileInput.disabled = true;
        HTMLFormElement.prototype.submit.call(this.form);
      } catch (error) {
        this.setBusy(false);
        this.showError(
          error instanceof Error ? error.message : 'De foto kon niet worden geüpload. Probeer het opnieuw.',
          this.fileInput,
        );
      }
    }

    setBusy(busy) {
      this.uploading = busy;
      this.submitButton.disabled = busy;
      this.submitButton.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.submitButton.textContent = busy ? 'Foto veilig uploaden…' : 'Verstuur mijn aanvraag';
    }

    clearError() {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    showError(message, focusTarget) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
      this.errorBox.focus();
      if (focusTarget) focusTarget.setAttribute('aria-invalid', 'true');
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-dpw-preview-service]').forEach((root) => {
      if (root.dataset.dpsInitialized === 'true') return;
      root.dataset.dpsInitialized = 'true';
      new DpwPreviewService(root).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
